import React, { useEffect, useState } from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

import { FilterButton } from '@insights_module/components/FilterButton';
import { ExportSelect } from '@insights_module/components/shows_screen/ExportsSelect';
import { AppliedFilters } from '@insights_module/components/filters/AppliedFilters';
import { Spacer } from '@components/Spacer';
import { StarIcon } from '@icons/StarIcon';
import { LevelsIcon } from '@icons/LevelsIcon';

import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { useViewsContext } from '@insights_module/context/ViewsContext';
import { useViews } from '@insights_module/hooks/useViews';
import { useInsightsParams } from '@insights_module/hooks/useInsightsParams';
import { SearchBar } from '@insights_module/components/SearchBar';
import { objectToToken } from '@utils/commonUtils';
import { EpisodesIconDark, EpisodesIconLight } from '@icons/EpisodesIcon';
import { ShowsIconDark, ShowsIconLight } from '@icons/ShowsIcon';
import { useGroupParams } from '@insights_module/hooks/useGroupParams';
import { useDebouncedState } from '@app/hooks/useDebounce';

const Container = tw.div`w-full h-[44px] flex items-start justify-between px-6 pt-2 pb-2 bg-neutral-background`;
const Flex = tw.div`w-auto flex items-center justify-center gap-2`;
const Separator = tw.div`h-[20px] w-[1px] bg-gray-1`;
const TableTypeToggleWrapper = tw.div`flex p-[2px] gap-[1px] rounded border border-gray-1 bg-gray-2`;
const TableTypeToggleButton = tw.div`flex py-[4px] px-[10px] justify-center items-center gap-[6px] rounded cursor-pointer`;
const ButtonTitle = tw.p`text-xs font-medium text-text-and-icon-muted`;

export const FILTER_HEIGHT = 44;

const FiltersButton = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation();
  const { selectedTableType } = useInsightsContext();
  return (
    <FilterButton onClick={onClick}>
      <LevelsIcon tw='fill-text-and-icon' />
      <Spacer direction='horizontal' size='xs' />
      {selectedTableType === 'shows'
        ? t('insights.filters_menu.show_filters')
        : t('insights.filters_menu.episode_filters')}
    </FilterButton>
  );
};

const ButtonToggler = ({
  onClick,
  isToggleActive = false,
  children,
}: {
  onClick: () => void;
  isToggleActive?: boolean;
  children: React.ReactNode;
}) => {
  return (
    <FilterButton
      onClick={onClick}
      css={[isToggleActive && tw`bg-accent-2 text-text-and-icon-invert`]}>
      {children}
    </FilterButton>
  );
};

export const FiltersMenu = (): JSX.Element | null => {
  const { t } = useTranslation();
  const {
    selectedTableType,
    setSelectedTableType,
    selectedShow,
    setShowFiltersModal,
    selectedEpisode,
  } = useInsightsContext();

  const {
    setShowViewsDrawer,
    setShowCreateViewModal,
    setViewToBeEdited,
    selectedView,
    setSelectedView,
    showViewsDrawer,
    isViewChanged,
  } = useViewsContext();

  const { setQueryToken } = useInsightsParams();

  const { groupParams, setGroupParams } = useGroupParams();
  const [searchTerm, setSearchTerm] = useState(groupParams?.name || '');
  const debouncedValue = useDebouncedState(searchTerm, 250);

  const { getViewById, getDefaultView } = useViews();
  const currentView = getViewById(selectedView as string);
  const defaultView = getDefaultView();

  useEffect(() => {
    setGroupParams({ name: debouncedValue?.length ? debouncedValue : undefined });
  }, [debouncedValue]);

  useEffect(() => {
    if (defaultView) {
      setSelectedView(defaultView.id);
    }
  }, [defaultView]);

  const handleSaveView = () => {
    setShowCreateViewModal(true);
  };

  const handleShowViews = () => {
    setShowViewsDrawer((val) => !val);
  };

  const handleSaveEdits = () => {
    setViewToBeEdited(selectedView);
  };

  const handleResetEdits = () => {
    if (!currentView) return;

    const query = JSON.parse(currentView.filters)?.query;
    if (query) {
      setQueryToken(objectToToken(query));
    }
  };

  const handleOpenFilters = () => {
    setShowFiltersModal(true);
  };

  if (selectedShow || selectedEpisode) return null;

  return (
    <Container data-testid='insights-filters-menu'>
      <Flex>
        <FiltersButton onClick={handleOpenFilters} />

        <AppliedFilters />

        {isViewChanged && (
          <>
            <Separator />
            <FilterButton onClick={handleResetEdits}>
              {t('insights.filters_menu.button.reset_edits')}
            </FilterButton>
            <FilterButton onClick={handleSaveEdits} tw='bg-muted'>
              {t('insights.filters_menu.button.save_edits')}
            </FilterButton>
          </>
        )}

        <SearchBar
          tw='h-[28px] w-[326px] text-[12px] placeholder:text-[12px] text-text-and-icon-muted-2 font-normal'
          onChange={(val) => setSearchTerm(val as string)}
          placeholder={
            selectedTableType === 'shows'
              ? t('insights.header.search_bar_placeholder')
              : t('insights.header.episodes_search_bar_placeholder')
          }
          value={searchTerm as string}
        />
      </Flex>

      <Flex>
        {!selectedView && (
          <FilterButton onClick={handleSaveView}>
            <StarIcon tw='stroke-text-and-icon' />
            <Spacer direction='horizontal' size='xs' />
            {t('insights.filters_menu.button.save_view')}
          </FilterButton>
        )}
        <ButtonToggler isToggleActive={showViewsDrawer} onClick={handleShowViews}>
          <LevelsIcon
            tw='fill-text-and-icon-invert'
            css={[showViewsDrawer ? tw`fill-text-and-icon` : tw`fill-text-and-icon`]}
          />
          <Spacer direction='horizontal' size='xs' />
          {t('insights.filters_menu.button.all_views')}
        </ButtonToggler>

        <Separator />
        <ExportSelect />

        <Separator />
        <TableTypeToggleWrapper>
          <TableTypeToggleButton
            css={[selectedTableType === 'shows' && tw`bg-accent-2`]}
            onClick={() => setSelectedTableType('shows')}>
            {selectedTableType === 'shows' ? <ShowsIconLight /> : <ShowsIconDark />}
            <ButtonTitle css={[selectedTableType === 'shows' && tw`text-text-and-icon-invert`]}>
              Shows
            </ButtonTitle>
          </TableTypeToggleButton>
          <TableTypeToggleButton
            css={[selectedTableType === 'episodes' && tw`bg-accent-2`]}
            onClick={() => setSelectedTableType('episodes')}>
            {selectedTableType === 'episodes' ? <EpisodesIconLight /> : <EpisodesIconDark />}
            <ButtonTitle css={[selectedTableType === 'episodes' && tw`text-text-and-icon-invert`]}>
              Episodes
            </ButtonTitle>
          </TableTypeToggleButton>
        </TableTypeToggleWrapper>
      </Flex>
    </Container>
  );
};
